import { useQueryLoader } from 'react-relay';

import logger from 'src/util/logger';

import getRouteUuidRetryVariables from './getRouteUuidRetryVariables';

/**
 * Shared retry plumbing for Relay route renderers
 * (e.g. `TradeListRoute`, `PropertyShowRoute`, `ProfileSettingsRoute`).
 * Wraps `useQueryLoader` so a route can switch from its initial found-relay
 * result to a freshly fetched query reference when the user retries after an
 * error. The retry contract is delegated to a route-specific helper via
 * `getRetryVariablesFunc`, which returns either a variables object (retry
 * enabled) or `null` (retry disabled).
 * @param {object} query - The Relay query to load on retry.
 * @param {object} routeProps - Found-relay route props (match/router/location).
 * @param {object} options - Hook options.
 * @param {string} options.logLabel - Label used when logging a skipped retry.
 * @param {Function} [options.getRetryVariablesFunc] - Route-specific helper
 *   returning either a variables object (retry enabled) or `null` (retry
 *   disabled). Defaults to `getRouteUuidRetryVariables` for UUID-keyed routes.
 * @returns {{queryRef: object, handleRetry: Function, retryDisabled: boolean}}
 *   The current query reference, the retry handler, and whether retry is disabled.
 */
export default function useRouteQueryRetry(
  query,
  routeProps,
  { logLabel, getRetryVariablesFunc = getRouteUuidRetryVariables },
) {
  const [queryRef, loadQuery] = useQueryLoader(query, null);
  const retryVariables = getRetryVariablesFunc(routeProps);
  const retryDisabled = retryVariables === null;

  const handleRetry = () => {
    if (retryVariables === null) {
      logger.error(`${logLabel} retry skipped because retry variables are missing.`);
      return;
    }

    loadQuery(retryVariables, {
      fetchPolicy: 'network-only',
    });
  };

  return { queryRef, handleRetry, retryDisabled };
}
